<template>
  <button :class="buttonClasses" @keydown.space.enter="isActive = true" @keyup.space.enter="isActive = false">
    <slot name="prepend" />
    <div class="k-btn__text-content truncate">
      <slot />
    </div>
    <slot name="append" />
    <KIcon v-if="isLoading" name="dots" class="h-10" />
  </button>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, computed } from 'vue'
import KIcon from '../Icon/index.vue'
export default defineComponent({
  name: 'KButton',
  components: {
    KIcon
  },
  props: {
    theme: {
      type: String as PropType<'primary' | 'secondary'>,
      required: false,
      validator: (value: string | undefined) =>
        !value || value === 'primary' || value === 'secondary'
    },
    textOnly: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    size: {
      type: String as PropType<'sm' | 'md' | 'lg' | 'xl'>,
      default: 'sm',
      validator: (value: string | undefined) =>
        !value || ['sm', 'md', 'lg', 'xl'].includes(value)
    }
  },
  setup(props) {
    const isActive = ref(false)
    const buttonClasses = computed(() => {
      return [
        'k-btn',
        `k-btn--${props.size}`,
        {
          'k-btn--box': !props.textOnly,
          'k-btn--text': props.textOnly,
          'k-btn--primary': props.theme === 'primary',
          'k-btn--secondary': props.theme === 'secondary',
          'k-btn--active': isActive.value || props.isLoading
        },
        'uppercase'
      ]
    })

    return {
      isActive,
      buttonClasses
    }
  }
})
</script>

<style lang="scss">
/* @import '@/src/assets/css/tailwind.css'; */
.k-btn {
  @apply flex items-center tracking-1.25px font-bold;

  &:focus {
    @apply outline-none;
  }

  body:not(.using-mouse) &:focus {
    @apply border-black-darkest border-2;
  }

  &__text-content {

    .k-btn--box &:after,
    .k-btn--box &:before {
      content: "";
      display: inline-block;
      width: 0;
    }
  }

  /* Button sizes */
  &--sm {
    @apply text-sm
  }

  &--md {
    @apply text-base
  }

  &--lg {
    @apply text-lg
  }

  &--xl {
    @apply text-xl
  }

  /* Box button */
  &--box {
    width: 276px;
    max-width: 100%;
    height: 44px;
    @apply justify-center box-border rounded px-4 transition-background-color duration-150 ease-in-out shadow-1-dp;

    &.k-btn--primary {
       @apply text-secondary bg-primary;
  
      @media (hover: hover) {
        &:hover {
          @apply bg-primary-hover
        }
      }
    }

    &.k-btn--secondary {
      @apply border outline-none text-primary;
   

      @media (hover: hover) {
        &:hover {
          @apply bg-secondary-hover
        }
      }
    }

    &:active,
    &.k-btn--active {
      &.k-btn--primary {
        @apply bg-primary-active
      }

      &.k-btn--secondary {
        @apply bg-secondary-active
      }
    }

    &:disabled {
      @apply bg-black-disabled pointer-events-none;
    }
  }

  /* Text button */
  &--text {
    max-width: 276px;
    @apply tracking-0.5px border-transparent border-2 font-normal;

    &.k-btn--primary {
      @apply text-primary-dark;
    }

    &.k-btn--secondary {
      @apply text-secondary;
    }

    &:hover {
      @apply underline;
    }

    &:active,
    &.k-btn--active {
      &.k-btn--primary {
        @apply text-primary-hover;
      }

      &.k-btn--secondary {
        @apply text-secondary-hover;
      }
    }

    &:disabled {
      @apply text-black-disabled pointer-events-none;
    }
  }
}
</style>
